import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HiXMark, HiCheckCircle } from 'react-icons/hi2';
import { addAudit } from '../../services/productAuditService';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { getAllSubmissions } from '../../services/productDevelopmentService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../../database/firebase';

const PROJECT_STAGES = [
  'Idea Validation',
  'Market Research',
  'Prototype Development',
  'Product Development',
  'MVP Launch',
];

const CreateAuditForm = ({ isOpen, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [organizations, setOrganizations] = useState(new Map());
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [formData, setFormData] = useState({
    submissionId: '',
    submissionTitle: '',
    projectStage: '',
    auditDate: new Date().toISOString().split('T')[0],
    findings: '',
    gaps: '',
    risks: '',
    improvementAreas: '',
    recommendations: '',
  });

  useEffect(() => {
    if (isOpen) {
      loadSubmissionsAndOrganizations();
    }
  }, [isOpen]);

  const loadSubmissionsAndOrganizations = async () => {
    setLoadingSubmissions(true);
    try {
      // Fetch all submissions - use currentUser role or default to admin for staff access
      const userRoleForQuery = currentUser?.role || 'admin';
      const submissionsData = await getAllSubmissions({
        userRole: ['admin', 'superAdmin', 'superAdmins'].includes(userRoleForQuery)
          ? userRoleForQuery
          : 'admin',
      });
      setSubmissions(submissionsData);

      // Fetch organizations from clients collection
      const orgMap = new Map();
      const clientEmails = new Set(submissionsData.map(s => s.userEmail).filter(Boolean));

      if (clientEmails.size > 0) {
        const clientsRef = collection(firestore, 'clients');
        const clientsSnapshot = await getDocs(clientsRef);

        clientsSnapshot.forEach(doc => {
          const clientData = doc.data();
          if (clientData.email && clientEmails.has(clientData.email)) {
            orgMap.set(clientData.email, clientData.organization || 'N/A');
          }
        });
      }

      setOrganizations(orgMap);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleSubmissionChange = e => {
    const submissionId = e.target.value;
    const submission = submissions.find(s => s.id === submissionId);
    setFormData(prev => ({
      ...prev,
      submissionId: submissionId,
      submissionTitle: submission?.title || '',
      projectStage: submission?.stageName || submission?.stage || '',
    }));
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.submissionId) {
      toast.error('Please select a submission');
      return;
    }

    setLoading(true);

    try {
      const submission = submissions.find(s => s.id === formData.submissionId);
      const organization = organizations.get(submission?.userEmail) || 'N/A';

      await addAudit(
        {
          ...formData,
          productName: formData.submissionTitle, // Keep for backward compatibility
          organization: organization,
          userEmail: submission?.userEmail || '',
        },
        {
          email: currentUser?.email || '',
          uid: currentUser?.uid || '',
          role:
            currentUser?.role ||
            (currentUser?.role === 'superAdmin' || currentUser?.role === 'superAdmins'
              ? 'superAdmin'
              : 'admin'),
          displayName: currentUser?.displayName || '',
        }
      );

      toast.success('Audit created successfully!');
      setFormData({
        submissionId: '',
        submissionTitle: '',
        projectStage: '',
        auditDate: new Date().toISOString().split('T')[0],
        findings: '',
        gaps: '',
        risks: '',
        improvementAreas: '',
        recommendations: '',
      });
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to create audit: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-xl font-semibold text-black">Create Product Audit</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Select Submission <span className="text-red-500">*</span>
              </label>
              {loadingSubmissions ? (
                <div className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-500">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                  Loading submissions...
                </div>
              ) : (
                <select
                  name="submissionId"
                  value={formData.submissionId}
                  onChange={handleSubmissionChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="">Select a submission...</option>
                  {submissions.map(submission => {
                    const org = organizations.get(submission.userEmail) || 'N/A';
                    return (
                      <option key={submission.id} value={submission.id}>
                        {submission.title || 'Untitled'} - {org} ({submission.category || 'N/A'})
                      </option>
                    );
                  })}
                </select>
              )}
              {formData.submissionTitle && (
                <p className="mt-2 text-xs text-gray-500">
                  Selected: <span className="font-medium">{formData.submissionTitle}</span>
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Project Stage <span className="text-red-500">*</span>
              </label>
              <select
                name="projectStage"
                value={formData.projectStage}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">Select stage</option>
                {PROJECT_STAGES.map(stage => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Audit Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="auditDate"
                value={formData.auditDate}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Findings</label>
              <textarea
                name="findings"
                value={formData.findings}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter audit findings"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Gaps</label>
              <textarea
                name="gaps"
                value={formData.gaps}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter identified gaps"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Risks</label>
              <textarea
                name="risks"
                value={formData.risks}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter identified risks"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Improvement Areas
              </label>
              <textarea
                name="improvementAreas"
                value={formData.improvementAreas}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter areas for improvement"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Recommendations
              </label>
              <textarea
                name="recommendations"
                value={formData.recommendations}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter recommendations"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Creating...
                </>
              ) : (
                <>
                  <HiCheckCircle className="h-5 w-5" />
                  Create Audit
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateAuditForm;
